<template>
  <section v-editable="blok">
    <div class="hidden-section">
      <div ref="hiddenContent" class="hidden-section__content">
        <div
          class="hidden-section__overlay"
          :class="{ 'hidden-section__overlay--open': isOpen }"
          :style="overlayBackgroundStyle"
        />
        <StoryblokComponent
          v-for="childBlok in blok.bloks"
          :key="childBlok._uid"
          :blok="childBlok"
        />
      </div>
      <div class="hidden-section__button" @click="toggleHide()">
        <div class="hidden-section__label">
          {{ !isOpen ? $t('common.see_more') : $t('common.hide') }}
        </div>
        <img
          width="16"
          height="16"
          src="@/assets/icons/16-chevron-down-navy-800.svg?url"
          alt=""
          class="hidden-section__chevron"
          :class="{ 'hidden-section__chevron--open': isOpen }"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { SbHiddenSectionStoryblok } from '@/types'
interface Props {
  blok: SbHiddenSectionStoryblok
}
const props = defineProps<Props>()

const hiddenContent = ref<HTMLElement>()
const isOpen = ref(false)
const initialHeight = ref('')

const openContent = () => {
  if (hiddenContent.value) {
    hiddenContent.value.style.maxHeight =
      hiddenContent.value.scrollHeight + 'px'
  }
}

const closeContent = () => {
  if (hiddenContent.value) {
    hiddenContent.value.style.maxHeight = initialHeight.value
  }
}

const toggleHide = () => {
  isOpen.value ? closeContent() : openContent()
  isOpen.value = !isOpen.value
}

const handleResize = () => {
  if (isOpen.value) {
    openContent()
  }
}

const overlayBackgroundStyle = computed(() => {
  return props.blok.overlay_color?.value
    ? `background: linear-gradient(to bottom, rgb(255 255 255 / 0%), ${props.blok.overlay_color?.value});`
    : 'background: linear-gradient(to bottom, rgb(255 255 255 / 0%), #fff);'
})

onMounted(() => {
  if (hiddenContent.value) {
    initialHeight.value = hiddenContent.value?.style.height
  }

  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="scss" scoped>
.hidden-section {
  &__content {
    position: relative;
    max-height: 210px;
    margin-bottom: 32px;
    transform: scaleY(1);
    overflow: hidden;
    transition: max-height 0.6s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  &__overlay {
    position: absolute;
    opacity: 1;
    width: 100%;
    height: 100%;
    transition: opacity 0.6s cubic-bezier(0.25, 0.8, 0.5, 1);

    &--open {
      opacity: 0;
      visibility: hidden;
    }
  }

  &__button {
    cursor: pointer;
    text-align: center;
  }

  &__label {
    margin-bottom: 9px;
    text-transform: uppercase;
    font-size: var(--font-size-paragraph-xs);
    color: var(--navy-700);
    font-weight: var(--font-weight-semibold);
  }

  &__chevron {
    transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);

    &--open {
      transform: rotate(180deg);
    }
  }
}
</style>
